import { useState, useRef, useEffect, type ReactNode, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Lock, ShieldAlert, Loader2 } from 'lucide-react';

const PIN_LENGTH = 4;

async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

interface OwnerPinGateProps {
  children: ReactNode;
}

export function OwnerPinGate({ children }: OwnerPinGateProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [verified, setVerified] = useState(false);
  const [pinRequired, setPinRequired] = useState<boolean | null>(null); // null = loading
  const [digits, setDigits] = useState<string[]>(Array(PIN_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Check if PIN is configured for this provider
  useEffect(() => {
    const checkPin = async () => {
      if (!user?.id) {
        setPinRequired(false);
        return;
      }
      const { data } = await supabase
        .from('provider_settings')
        .select('owner_pin_hash')
        .eq('provider_id', user.id)
        .maybeSingle();

      const storedHash = data?.owner_pin_hash || null;
      const fallbackPin = import.meta.env.VITE_OWNER_PIN;

      // PIN only required if there's a hash in DB or a fallback env var
      setPinRequired(!!(storedHash || fallbackPin));
    };
    checkPin();
  }, [user?.id]);

  useEffect(() => {
    if (pinRequired && !verified) {
      inputsRef.current[0]?.focus();
    }
  }, [pinRequired, verified]);

  // Still loading
  if (pinRequired === null) return null;

  // No PIN configured → pass through
  if (!pinRequired || verified) return <>{children}</>;

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    setError('');
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    if (value && index < PIN_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (!pasted) return;
    const next = Array(PIN_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, PIN_LENGTH - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleSubmit = async () => {
    const pin = digits.join('');
    if (pin.length < PIN_LENGTH) {
      setError('Introduce los 4 dígitos');
      return;
    }

    setChecking(true);
    setError('');

    try {
      let storedHash: string | null = null;
      if (user?.id) {
        const { data } = await supabase
          .from('provider_settings')
          .select('owner_pin_hash')
          .eq('provider_id', user.id)
          .maybeSingle();
        storedHash = data?.owner_pin_hash || null;
      }

      let isValid = false;
      if (storedHash) {
        const enteredHash = await sha256(pin);
        isValid = enteredHash === storedHash;
      } else {
        const fallbackPin = import.meta.env.VITE_OWNER_PIN;
        isValid = pin === fallbackPin;
      }

      if (isValid) {
        setVerified(true);
      } else {
        setError('PIN incorrecto');
        setDigits(Array(PIN_LENGTH).fill(''));
        inputsRef.current[0]?.focus();
      }
    } catch {
      setError('Error al verificar PIN');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-[#FDF6E3] flex items-center justify-center mx-auto mb-5">
          <Lock className="h-7 w-7 text-[#B8860B]" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Área restringida</h2>
        <p className="text-sm text-gray-500 mb-6">Introduce el PIN de propietaria</p>
        <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputsRef.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 border-gray-200 bg-gray-50 focus:border-[#B8860B] focus:ring-2 focus:ring-[#B8860B]/20 focus:bg-white outline-none transition-all"
            />
          ))}
        </div>
        {error && (
          <div className="flex items-center justify-center gap-2 text-red-600 text-sm mb-4">
            <ShieldAlert className="h-4 w-4" />
            {error}
          </div>
        )}
        <Button
          onClick={handleSubmit}
          disabled={checking}
          className="w-full bg-[#B8860B] hover:bg-[#996F0A] text-white h-12 text-base font-semibold"
        >
          {checking ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Confirmar'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="w-full mt-2 text-gray-500 hover:text-gray-700 h-10"
        >
          Volver
        </Button>
      </div>
    </div>
  );
}